<script setup lang="ts">
import { computed } from 'vue'
import type { ConflictNoticeLabels } from './locales/conflictMessages'
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  layout?: 'dialog' | 'page'
  variant?: 'default' | 'structure' | 'entra-503'
  changed?: readonly string[]
  labels: ConflictNoticeLabels
  busy?: boolean
}>(), { layout: 'dialog', variant: 'default', changed: () => [], busy: false })
const emit = defineEmits<{ reload: [] }>()
const message = computed(() => props.labels[props.variant])
</script>

<template>
  <div
    v-bind="$attrs"
    role="alert"
    data-bee-component="conflict-notice"
    :data-bee-state="variant === 'entra-503' ? 'entra-unavailable' : 'conflict'"
    :data-layout="layout"
    class="fw-conflict-notice"
  >
    <span aria-hidden="true">⚠</span>
    <span class="fw-conflict-copy">
      <b>{{ message.title }}</b>
      <span>{{ message.body }}</span>
      <span
        v-if="changed.length"
        class="fw-conflict-changed"
      >{{ labels.changed }} {{ changed.join(', ') }}</span>
    </span>
    <button
      v-if="variant !== 'entra-503'"
      type="button"
      :aria-disabled="busy"
      @click="!busy && emit('reload')"
    >
      {{ message.action }}
    </button>
  </div>
  <p
    v-if="busy"
    role="status"
    aria-live="polite"
    class="fw-conflict-loading"
  >
    {{ labels.loading }}
  </p>
</template>

<style scoped>
.fw-conflict-notice { display:flex;gap:10px;align-items:flex-start;flex-wrap:wrap;padding:10px 12px;border-radius:9px;border:1px solid rgba(210,153,34,.45);background:rgba(210,153,34,.08);color:var(--fw-text-primary);font-size:13px;line-height:1.5; }
.fw-conflict-notice[data-layout="page"] { padding:12px 14px; }
.fw-conflict-notice>[aria-hidden] { color:var(--fw-warning-fg); }
.fw-conflict-copy { flex:1 1 200px;min-width:0;overflow-wrap:anywhere;color:var(--fw-text-secondary); }
.fw-conflict-copy>b { display:block;font-weight:600;color:var(--fw-text-primary); }
.fw-conflict-changed,.fw-conflict-loading { display:block;margin-top:4px; }
.fw-conflict-loading { margin:4px 0 0;color:var(--fw-text-secondary);font-size:13px;line-height:1.5; }
.fw-conflict-notice>button { flex:none;min-height:30px;max-width:100%;padding:0 12px;border-radius:7px;border:1px solid var(--fw-ctl);background:transparent;color:var(--fw-text-primary);font:inherit;cursor:pointer;white-space:normal; }
.fw-conflict-notice>button:focus-visible { outline:2px solid #3B6FE8;outline-offset:2px; }
.fw-conflict-notice>button[aria-disabled="true"] { color:var(--fw-text-secondary);opacity:.7;cursor:default; }
</style>
